import { Capacitor } from '@capacitor/core'
import maplibregl from 'maplibre-gl'
import { booleanPointInPolygon, point as turfPoint } from '@turf/turf'
import type { Feature, FeatureCollection, Polygon, MultiPolygon } from 'geojson'
import { logger } from '../../utils/logger.js'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import type {
  AvalancheProblemType,
  AvalancheResult,
  AvalancheForecast,
  ForecastPeriod,
  AvalancheProblem,
  ElevationDanger,
  ElevationBand,
  Aspect,
  DangerLevel,
  AspectElevation,
} from './avalanche-types.js'
import { timelineService, type TimelineState } from '../timeline/timeline-service.js'

const log = logger.create('avalanche-layers')

const DATA_ORIGIN = Capacitor.isNativePlatform() ? 'https://taselith.com' : ''
const BASE_URL = `${DATA_ORIGIN}/data/avalanche`
const CACHE_TTL_MS = 30 * 60 * 1000
const CACHE_MAX_ENTRIES = 14

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const SOURCE_ID = 'avalanche-zones'
const GROUP_ID = 'avalanche-zones'

export const ALL_AVALANCHE_PROBLEMS: AvalancheProblemType[] = [
  'storm-slab',
  'wind-slab',
  'persistent-slab',
  'deep-slab',
  'wet-loose',
  'wet-slab',
  'dry-loose',
  'cornice',
  'glide',
]

interface CachedData {
  data: FeatureCollection
  fetchedAt: number
}

export interface AvalancheZoneProperties {
  name: string
  center: string
  center_id: string
  center_link: string
  state: string
  danger_level: number
  danger: string
  travel_advice: string
  link: string | null
  off_season: boolean
  start_date: string | null
  end_date: string | null
  danger_upper: number | null
  danger_middle: number | null
  danger_lower: number | null
  bottom_line: string | null
  forecast_problems: Array<{
    type: string
    likelihood: string | null
    size: string | null
    locations: string[]
  }>
}

const LAYER_IDS = {
  fill: 'avalanche-zones-fill',
  line: 'avalanche-zones-line',
  label: 'avalanche-zones-label',
} as const

const LAYER_SPECS: maplibregl.LayerSpecification[] = [
  {
    id: LAYER_IDS.fill,
    type: 'fill',
    source: SOURCE_ID,
    paint: {
      'fill-color': [
        'case',
        ['==', ['get', 'off_season'], true],
        '#94a3b8',
        ['==', ['get', 'danger_level'], -1],
        '#3b82f6',
        ['==', ['get', 'danger_level'], 0],
        '#cccccc',
        ['==', ['get', 'danger_level'], 1],
        '#50b848',
        ['==', ['get', 'danger_level'], 2],
        '#fff200',
        ['==', ['get', 'danger_level'], 3],
        '#f7941e',
        ['==', ['get', 'danger_level'], 4],
        '#ed1c24',
        ['==', ['get', 'danger_level'], 5],
        '#231f20',
        '#e2e8f0',
      ],
      'fill-opacity': 0.35,
    },
  } as maplibregl.FillLayerSpecification,
  {
    id: LAYER_IDS.line,
    type: 'line',
    source: SOURCE_ID,
    paint: {
      'line-color': [
        'case',
        ['==', ['get', 'off_season'], true],
        '#64748b',
        ['==', ['get', 'danger_level'], -1],
        '#2563eb',
        ['==', ['get', 'danger_level'], 0],
        '#999999',
        ['==', ['get', 'danger_level'], 1],
        '#3d8c37',
        ['==', ['get', 'danger_level'], 2],
        '#c4b800',
        ['==', ['get', 'danger_level'], 3],
        '#c47418',
        ['==', ['get', 'danger_level'], 4],
        '#b8161d',
        ['==', ['get', 'danger_level'], 5],
        '#000000',
        '#94a3b8',
      ],
      'line-width': 1.5,
      'line-opacity': 1,
    },
  } as maplibregl.LineLayerSpecification,
  {
    id: LAYER_IDS.label,
    type: 'symbol',
    source: SOURCE_ID,
    minzoom: 7,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 11,
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#1e293b',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
      'text-opacity': 1,
    },
  } as maplibregl.SymbolLayerSpecification,
]

class AvalancheLayerService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = 1.0
  private _minDanger = 0
  private _problemTypes = new Set<AvalancheProblemType>()
  private _date: string = new Date().toISOString().slice(0, 10)
  private cache = new Map<string, CachedData>()
  private inflight = new Map<string, Promise<FeatureCollection | null>>()

  private timelineListener = (state: Readonly<TimelineState>): void => {
    const date = state.currentTime.toISOString().split('T')[0]
    this.setDate(date)
  }

  // ── Lifecycle ──

  register(map: maplibregl.Map): void {
    this.map = map
    timelineService.addListener(this.timelineListener)
    this.setDate(timelineService.getCurrentDateISO())
  }

  unregister(): void {
    timelineService.removeListener(this.timelineListener)
    this.map = null
  }

  reAdd(): void {
    if (!this.map || !this._enabled) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.addLayers()
      void this.loadData()
    }
    this.applyOpacity()
    this.applyFilters()
  }

  // ── Toggle ──

  get enabled(): boolean {
    return this._enabled
  }

  enable(): void {
    if (this._enabled) return
    this._enabled = true
    if (!this.map) return
    this.addLayers()
    void this.loadData()
  }

  disable(): void {
    if (!this._enabled) return
    this._enabled = false
    this.removeLayers()
  }

  toggle(): void {
    if (this._enabled) this.disable()
    else this.enable()
  }

  // ── Date ──

  get date(): string {
    return this._date
  }

  setDate(date: string): void {
    if (date === this._date) return
    this._date = date
    if (this._enabled) void this.loadData()
  }

  // ── Opacity ──

  get opacity(): number {
    return this._opacity
  }

  setOpacity(opacity: number): void {
    this._opacity = opacity
    this.applyOpacity()
  }

  private applyOpacity(): void {
    if (!this.map) return
    if (this.map.getLayer(LAYER_IDS.fill))
      this.map.setPaintProperty(LAYER_IDS.fill, 'fill-opacity', this._opacity * 0.35)
    if (this.map.getLayer(LAYER_IDS.line))
      this.map.setPaintProperty(LAYER_IDS.line, 'line-opacity', this._opacity)
    if (this.map.getLayer(LAYER_IDS.label))
      this.map.setPaintProperty(LAYER_IDS.label, 'text-opacity', this._opacity)
  }

  // ── Danger threshold filter ──

  get minDanger(): number {
    return this._minDanger
  }

  setMinDanger(level: number): void {
    this._minDanger = level
    this.applyFilters()
  }

  // ── Problem filter ──

  get problemFilter(): ReadonlySet<AvalancheProblemType> {
    return this._problemTypes
  }

  setProblemFilter(types: AvalancheProblemType[]): void {
    this._problemTypes = new Set(types)
    this.applyFilters()
  }

  toggleProblem(type: AvalancheProblemType): void {
    if (this._problemTypes.has(type)) this._problemTypes.delete(type)
    else this._problemTypes.add(type)
    this.applyFilters()
  }

  // ── Filter application ──

  private applyFilters(): void {
    if (!this.map || !this._enabled) return

    const filters: maplibregl.ExpressionSpecification[] = []

    if (this._minDanger > 0) {
      filters.push(['>=', ['get', 'danger_level'], this._minDanger])
    }

    if (this._problemTypes.size > 0) {
      const problemFilter: maplibregl.ExpressionSpecification = [
        'any',
        ...[...this._problemTypes].map(
          t =>
            [
              '==',
              ['get', `has_${t.replace(/-/g, '_')}`],
              true,
            ] as maplibregl.ExpressionSpecification
        ),
      ]
      filters.push(problemFilter)
    }

    const combined: maplibregl.FilterSpecification | null =
      filters.length === 0
        ? null
        : filters.length === 1
          ? (filters[0] as maplibregl.FilterSpecification)
          : (['all', ...filters] as unknown as maplibregl.FilterSpecification)

    for (const id of Object.values(LAYER_IDS)) {
      if (this.map.getLayer(id)) {
        this.map.setFilter(id, combined)
      }
    }
  }

  // ── Point-in-polygon ──

  resolveZone(lat: number, lon: number): AvalancheZoneProperties | null {
    return this.resolveZoneForDate(lat, lon, this._date)
  }

  resolveZoneForDate(lat: number, lon: number, date: string): AvalancheZoneProperties | null {
    const cached = this.cache.get(date)
    if (!cached) return null

    const pt = turfPoint([lon, lat])
    for (const feature of cached.data.features) {
      const geom = feature.geometry
      if (geom.type !== 'Polygon' && geom.type !== 'MultiPolygon') continue
      if (booleanPointInPolygon(pt, feature as Feature<Polygon | MultiPolygon>)) {
        return feature.properties as AvalancheZoneProperties
      }
    }
    return null
  }

  // ── Public forecast API (replaces old Lambda-backed AvalancheService) ──

  async getForecast(
    lat: number,
    lon: number,
    startDate: string,
    endDate: string,
    forceRefresh = false
  ): Promise<AvalancheResult | null> {
    const dates = this.dateRange(startDate, endDate)
    const periods: ForecastPeriod[] = []
    let source: AvalancheForecast['source'] | null = null

    for (const date of dates) {
      if (forceRefresh) {
        await this.fetchDate(date)
      } else {
        await this.ensureDate(date)
      }
      const zone = this.resolveZoneForDate(lat, lon, date)
      if (!zone) continue

      if (!source) {
        source = {
          centerId: zone.center_id,
          centerName: zone.center,
          zone: zone.name,
          link: zone.link ?? '',
          adapter: 'avalanche-org',
        }
      }

      periods.push(this.zoneToPeriod(zone, date))
    }

    if (!source) return null

    const data: AvalancheForecast = {
      source,
      timing: {
        requestedStart: startDate,
        requestedEnd: endDate,
        issued: new Date().toISOString(),
        expires: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
        coverage:
          periods.length === dates.length ? 'full' : periods.length > 0 ? 'partial' : 'no-forecast',
        coveredDates: periods.map(p => p.date),
      },
      periods,
      detailLevel: 'full',
    }

    return { data, fresh: true, fetchedAt: new Date().toISOString() }
  }

  async ensureDate(date: string): Promise<void> {
    const cached = this.cache.get(date)
    const now = Date.now()
    const today = new Date().toISOString().slice(0, 10)
    const isPast = date < today

    if (cached && (isPast || now - cached.fetchedAt < CACHE_TTL_MS)) {
      return
    }

    if (cached) {
      void this.fetchDate(date)
      return
    }

    await this.fetchDate(date)
  }

  // ── Click detection ──

  queryFeatures(point: maplibregl.Point): maplibregl.MapGeoJSONFeature[] {
    if (!this.map) return []
    if (!this.map.getLayer(LAYER_IDS.fill)) return []
    return this.map.queryRenderedFeatures(point, { layers: [LAYER_IDS.fill] })
  }

  getSelectableLayerIds(): string[] {
    if (!this._enabled) return []
    return [LAYER_IDS.fill]
  }

  // ── Data loading ──

  private async loadData(): Promise<void> {
    await this.ensureDate(this._date)
    const cached = this.cache.get(this._date)
    if (cached) this.setSourceData(cached.data)
  }

  private async fetchDate(date: string): Promise<FeatureCollection | null> {
    const existing = this.inflight.get(date)
    if (existing) return existing

    const promise = this.doFetch(date)
    this.inflight.set(date, promise)
    try {
      return await promise
    } finally {
      this.inflight.delete(date)
    }
  }

  private async doFetch(date: string): Promise<FeatureCollection | null> {
    const url = `${BASE_URL}/${date}.geojson`
    try {
      const resp = await fetch(url)
      if (!resp.ok) {
        log.warn(`Failed to fetch avalanche data for ${date}: ${resp.status}`)
        return null
      }
      const data = (await resp.json()) as FeatureCollection
      this.cacheSet(date, data)
      if (date === this._date) this.setSourceData(data)
      return data
    } catch (err) {
      log.warn(`Failed to fetch avalanche data for ${date}:`, err)
      return null
    }
  }

  private cacheSet(date: string, data: FeatureCollection): void {
    if (this.cache.has(date)) {
      this.cache.delete(date)
    }
    this.cache.set(date, { data, fetchedAt: Date.now() })

    while (this.cache.size > CACHE_MAX_ENTRIES) {
      const oldest = this.cache.keys().next().value!
      this.cache.delete(oldest)
    }
  }

  private setSourceData(data: FeatureCollection): void {
    if (!this.map) return
    const source = this.map.getSource(SOURCE_ID)
    if (source) (source as maplibregl.GeoJSONSource).setData(data)
  }

  // ── Zone → ForecastPeriod conversion ──

  private zoneToPeriod(zone: AvalancheZoneProperties, date: string): ForecastPeriod {
    const dangerByElevation: ElevationDanger[] = []
    if (zone.danger_upper != null)
      dangerByElevation.push({ band: 'above-treeline', level: zone.danger_upper as DangerLevel })
    if (zone.danger_middle != null)
      dangerByElevation.push({ band: 'near-treeline', level: zone.danger_middle as DangerLevel })
    if (zone.danger_lower != null)
      dangerByElevation.push({ band: 'below-treeline', level: zone.danger_lower as DangerLevel })

    const problems: AvalancheProblem[] = zone.forecast_problems.map(p => {
      const { aspects, elevations, locations } = this.parseLocations(p.locations)
      return {
        type: p.type as AvalancheProblemType,
        aspects,
        elevations,
        locations,
        likelihood: p.likelihood ?? '',
        expectedSize: p.size ?? '',
      }
    })

    return {
      date,
      overallDanger: zone.danger_level as DangerLevel,
      dangerByElevation,
      problems,
      travelAdvice: zone.travel_advice ?? '',
      summary: zone.bottom_line ?? '',
    }
  }

  private parseLocations(raw: string[]): {
    aspects: Aspect[]
    elevations: ElevationBand[]
    locations: AspectElevation[]
  } {
    const ASPECT_MAP: Record<string, Aspect> = {
      north: 'N',
      northeast: 'NE',
      east: 'E',
      southeast: 'SE',
      south: 'S',
      southwest: 'SW',
      west: 'W',
      northwest: 'NW',
    }
    const ELEV_MAP: Record<string, ElevationBand> = {
      upper: 'above-treeline',
      middle: 'near-treeline',
      lower: 'below-treeline',
    }

    const aspectSet = new Set<Aspect>()
    const elevSet = new Set<ElevationBand>()
    const locations: AspectElevation[] = []

    for (const loc of raw) {
      const parts = loc.toLowerCase().split(' ')
      const elev = parts.pop()
      const aspect = parts.join('')
      const mappedAspect = ASPECT_MAP[aspect]
      const mappedElev = elev ? ELEV_MAP[elev] : undefined
      if (mappedAspect && mappedElev) {
        aspectSet.add(mappedAspect)
        elevSet.add(mappedElev)
        locations.push({ aspect: mappedAspect, elevation: mappedElev })
      }
    }

    return {
      aspects: [...aspectSet],
      elevations: [...elevSet],
      locations,
    }
  }

  private dateRange(start: string, end: string): string[] {
    const dates: string[] = []
    const current = new Date(start + 'T12:00:00Z')
    const last = new Date(end + 'T12:00:00Z')
    while (current <= last) {
      dates.push(current.toISOString().slice(0, 10))
      current.setUTCDate(current.getUTCDate() + 1)
    }
    return dates
  }

  // ── Layer management ──

  private addLayers(): void {
    if (!this.map) return
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, { type: 'geojson', data: EMPTY_GEOJSON })
    }
    for (const spec of LAYER_SPECS) {
      if (!this.map.getLayer(spec.id)) {
        addOverlayLayer(this.map, spec, GROUP_ID)
      }
    }
  }

  private removeLayers(): void {
    if (!this.map) return
    for (const spec of [...LAYER_SPECS].reverse()) {
      if (this.map.getLayer(spec.id)) this.map.removeLayer(spec.id)
    }
    if (this.map.getSource(SOURCE_ID)) this.map.removeSource(SOURCE_ID)
  }
}

export const avalancheLayerService = new AvalancheLayerService()
